/**
 * "Is this element's address dub audio, or the separated music bed?"
 *
 * Address tests rather than a stored list of element identifiers. The dub is a
 * DERIVED value now (PLAT-11511): nothing on the stored timeline names the
 * elements that carry it, so the only way to recognise dub audio that a clip
 * saved before this change still carries is the address it plays from.
 *
 * Both patterns are the backend's own, kept identical to the tests used by the
 * code that re-mints those links at render time, so this side's idea of what
 * dub audio is cannot drift from that one's.
 */

/** Generated dubbed speech. */
const DUB_KEY_PATTERN = /^sessions\/[^/]+\/dubbing\//;

/**
 * A separated stem — the recording's own music, laid under a dub. Narrower than
 * the backend's `video-source-audio/` test on purpose: that prefix also holds
 * the untouched source audio and the decoded working copy, neither of which is
 * a bed.
 */
const STEM_KEY_PATTERN = /^sessions\/[^/]+\/video-source-audio\/stems\//;

/**
 * Matched on the path only — hosts differ per environment.
 *
 * The cheap text test comes first and the address is only taken apart when that
 * passes. This runs over every audio element every time a document is produced,
 * which on the preview side is on every editor change and all the way through
 * scrubbing, so the ordinary answer of "no" must cost almost nothing.
 */
function keyPath(source: unknown, marker: string): string | null {
  if (typeof source !== 'string' || source === '') return null;
  if (!source.includes(marker)) return null;
  let path = source;
  try {
    path = new URL(source).pathname;
  } catch {
    // Not an absolute URL — fall through and test the raw string.
  }
  return path.replace(/^\/+/, '');
}

function sourceMatches(
  source: unknown,
  pattern: RegExp,
  marker: string,
): boolean {
  const path = keyPath(source, marker);
  return path !== null && pattern.test(path);
}

export function isDubAudioSource(source: unknown): boolean {
  return sourceMatches(source, DUB_KEY_PATTERN, '/dubbing/');
}

export function isMusicBedSource(source: unknown): boolean {
  return sourceMatches(source, STEM_KEY_PATTERN, '/stems/');
}

/** The language segment of a dub address, and whatever follows it. */
const DUB_LANGUAGE_PATTERN = /^sessions\/[^/]+\/dubbing\/([^/]+)\//;

/**
 * A language tag, as far as this needs to know one: a two or three letter
 * language, then any number of dash-joined subtags (region, script, variant).
 * Deliberately a shape test and not a list of languages — the list of languages
 * a story can be dubbed into is the backend's, and a copy of it here would go
 * stale the first time one was added.
 */
const LANGUAGE_TAG_PATTERN = /^[A-Za-z]{2,3}(?:-[A-Za-z0-9]{2,8})*$/;

/**
 * "Which language does this address's audio speak?"
 *
 * The backend writes the target language straight into the key it stores a
 * dub's audio under — `sessions/<session>/dubbing/<language>/…` — so the
 * address is a second, independent record of the language, and the only one a
 * clip saved before PLAT-11511 still carries once its stored dub record is
 * gone.
 *
 * The segment is returned exactly as written, casing and all. It is looked up
 * by exact match against the language the dub description is held under, so
 * tidying `en-us` into `en-US` would turn a hit into a miss.
 *
 * `null` for everything else, and never a guess: the music bed's address (which
 * belongs to no language), a dub address with no language segment in it, and a
 * segment that is not shaped like a language tag all answer nothing. A language
 * invented here would name a dub that cannot be rebuilt.
 */
export function dubLanguageFromSource(source: unknown): string | null {
  const path = keyPath(source, '/dubbing/');
  if (path === null) return null;
  const language = DUB_LANGUAGE_PATTERN.exec(path)?.[1];
  if (!language || !LANGUAGE_TAG_PATTERN.test(language)) return null;
  return language;
}
